import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '@/services/api.service';
import {
  Card,
  Form,
  Input,
  Button,
  Select,
  DatePicker,
  InputNumber,
  Space,
  Typography,
  Row,
  Col,
  Table,
  Divider,
  message,
  Spin,
  Switch,
  Tag
} from 'antd';
import {
  ArrowLeftOutlined,
  SaveOutlined,
  PlusOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useSalesOrder, useCreateSalesOrder, useUpdateSalesOrder } from '../../hooks/useSalesOrder';
import { useCustomers } from '../../hooks/useCustomers';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useCurrency } from '@/contexts/CurrencyContext';
import type { SalesOrderMilestone } from '../../services/salesOrder.service';
import { ProjectSelector } from '@/components/project';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import { useApplyUserDefaultDepartment } from '@/utils/userDefaultDepartment';
import quotationService from '@/services/quotation.service';
import salesOrderService from '@/services/salesOrder.service';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

/** Western digits for money display (NM_WP_9 — avoid locale Arabic-Indic numerals in UI). */
const fmtEnMoney = (n: number) =>
  n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

interface MilestoneRowData {
  key: string;
  milestone_number: number;
  milestone_name: string;
  description?: string;
  planned_date?: string;
  milestone_value: number;
  billing_percentage: number;
  milestone_status: string;
}

interface SalesOrderItemFormData {
  key: string;
  /** Persisted line id — required for backend to remove/update lines correctly */
  id?: string;
  item_code: string;
  item_description: string;
  revision?: string;
  item_category?: string;
  quantity: number;
  uom: string;
  unit_price: number;
  discount_percentage: number;
  vat_applicable: boolean;
  vat_percentage: number;
  notes?: string;
  specifications?: string;
  delivery_days?: number;
  line_total?: number;
  net_amount?: number;
}

const SalesOrderFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { baseCurrency, formatCurrency } = useCurrency();
  const watchedCustomerId = Form.useWatch('customer_id', form);
  const watchedQuotationId = Form.useWatch('quotation_id', form);
  const orderCurrency = Form.useWatch('currency', form) || baseCurrency?.currency_code || 'AED';

  const isEditMode = !!id;
  useApplyUserDefaultDepartment(form, !isEditMode);

  const { data: quotationsForSo = [] } = useQuery({
    queryKey: ['quotations', 'sales-order-create', watchedCustomerId],
    queryFn: () =>
      quotationService.getAll({
        customer_id: watchedCustomerId!,
        status: 'Accepted',
        is_latest_revision: true,
      }),
    enabled: !isEditMode && Boolean(watchedCustomerId),
  });
  const quotationOptions = useMemo(() => {
    return quotationsForSo
      .filter(
        (q) =>
          (String(q.estimation_id || '').trim() || String(q.boq_id || '').trim()) &&
          !q.is_converted_to_sales_order
      )
      .map((q) => ({
        value: q.id,
        label: `${q.quotation_number}${q.quotation_name ? ` — ${q.quotation_name}` : ''}`,
      }));
  }, [quotationsForSo]);

  const { data: orderData, isLoading: orderLoading } = useSalesOrder(id!);
  const { data: orderPhaseBCheck } = useQuery({
    queryKey: ['sales-order-status-check', id],
    queryFn: () => salesOrderService.getOrderStatusCheck(id!),
    enabled: isEditMode && Boolean(id),
    staleTime: 30_000,
  });
  const hasLinkedWorkOrders = (orderPhaseBCheck?.work_orders?.length ?? 0) > 0;
  const { data: customersData } = useCustomers();
  const createMutation = useCreateSalesOrder();
  const updateMutation = useUpdateSalesOrder();

  const [lineItems, setLineItems] = useState<SalesOrderItemFormData[]>([]);
  const [subtotal, setSubtotal] = useState(0);
  const [discountPercentage, setDiscountPercentage] = useState(0);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [totalAfterDiscount, setTotalAfterDiscount] = useState(0);
  const [vatApplicable, setVatApplicable] = useState(true);
  const [vatPercentage, setVatPercentage] = useState(5);
  const [vatAmount, setVatAmount] = useState(0);
  const [grandTotal, setGrandTotal] = useState(0);
  const [advancePaymentPercentage, setAdvancePaymentPercentage] = useState(0);
  const [advancePaymentAmount, setAdvancePaymentAmount] = useState(0);
  const [retentionPercentage, setRetentionPercentage] = useState(0);
  const [retentionAmount, setRetentionAmount] = useState(0);
  const [isMilestoneBased, setIsMilestoneBased] = useState(false);
  const [milestoneRows, setMilestoneRows] = useState<MilestoneRowData[]>([]);

  const { data: revenueServiceTypes = [] } = useQuery({
    queryKey: ['sales-revenue-service-types', 'active'],
    queryFn: async () => {
      const res = await api.get('/sales/revenue-service-types?only_active=true');
      const body = (res as any)?.data ?? res;
      const list = Array.isArray(body) ? body : Array.isArray(body?.data) ? body.data : [];
      return list as { id: string; service_code: string; service_name: string }[];
    },
  });
  const rstOptions = useMemo(
    () => revenueServiceTypes.map((r) => ({ value: r.id, label: `${r.service_code} — ${r.service_name}` })),
    [revenueServiceTypes]
  );

  const salesOrder = orderData;
  const customers =
    (Array.isArray(customersData)
      ? customersData
      : (customersData as { data?: unknown[] } | undefined)?.data) ?? [];

  // Helper function to safely convert to number
  const toNumber = (value: any): number => {
    if (value === null || value === undefined) return 0;
    const num = Number(value);
    return isNaN(num) ? 0 : num;
  };

  // Load sales order data in edit mode
  useEffect(() => {
    if (!isEditMode && watchedCustomerId) {
      form.setFieldValue('quotation_id', undefined);
    }
  }, [watchedCustomerId, isEditMode, form]);

  useEffect(() => {
    console.log('📝 [SalesOrderFormPage] Edit mode:', isEditMode, 'Sales Order:', salesOrder);
    if (isEditMode && salesOrder) {
      form.setFieldsValue({
        customer_id: salesOrder.customer_id,
        customer_name: salesOrder.customer_name,
        project_id: salesOrder.project_id || undefined,
        project_name: salesOrder.project_name,
        department_id: salesOrder.department_id || undefined,
        customer_contact: salesOrder.customer_contact,
        customer_email: salesOrder.customer_email,
        customer_phone: salesOrder.customer_phone,
        order_date: salesOrder.order_date ? dayjs(salesOrder.order_date) : dayjs(),
        expected_delivery_date: salesOrder.expected_delivery_date ? dayjs(salesOrder.expected_delivery_date) : null,
        currency: salesOrder.currency,
        payment_terms: salesOrder.payment_terms,
        delivery_terms: salesOrder.delivery_terms,
        customer_po_number: salesOrder.customer_po_number,
        customer_po_date: salesOrder.customer_po_date ? dayjs(salesOrder.customer_po_date) : null,
        shipping_address: salesOrder.delivery_address || salesOrder.shipping_address,
        billing_address: salesOrder.customer_address || salesOrder.billing_address,
        vat_applicable: salesOrder.vat_applicable,
        vat_percentage: toNumber(salesOrder.vat_percentage),
        discount_percentage: toNumber(salesOrder.discount_percentage),
        notes: salesOrder.notes,
        special_instructions: salesOrder.special_instructions,
        commercial_scope_text: (salesOrder as any).commercial_scope_text,
        quality_assurance_text: (salesOrder as any).quality_assurance_text,
        customer_requirements_text: (salesOrder as any).customer_requirements_text,
        revenue_service_type_id: salesOrder.revenue_service_type_id || undefined,
      });

      setVatApplicable(salesOrder.vat_applicable ?? true);
      setVatPercentage(toNumber(salesOrder.vat_percentage));
      setDiscountPercentage(toNumber(salesOrder.discount_percentage));
      setAdvancePaymentPercentage(toNumber(salesOrder.advance_payment_percentage));
      setAdvancePaymentAmount(toNumber(salesOrder.advance_payment_amount));
      setRetentionPercentage(toNumber(salesOrder.retention_percentage));
      setRetentionAmount(toNumber(salesOrder.retention_amount));
      setIsMilestoneBased(!!salesOrder.is_milestone_based);
      if (salesOrder.milestones && salesOrder.milestones.length > 0) {
        setMilestoneRows(
          salesOrder.milestones.map((m: SalesOrderMilestone, idx: number) => ({
            key: `ms-${m.id || idx}`,
            milestone_number: m.milestone_number ?? idx + 1,
            milestone_name: m.milestone_name || '',
            description: m.description,
            planned_date: m.planned_date,
            milestone_value: toNumber(m.milestone_value),
            billing_percentage: toNumber(m.billing_percentage),
            milestone_status: m.milestone_status || 'Planned',
          }))
        );
      } else {
        setMilestoneRows([]);
      }

      if (salesOrder.items && salesOrder.items.length > 0) {
        const items = salesOrder.items.map((item: any, index: number) => ({
          key: item.id ? `item-${item.id}` : `item-${index}`,
          id: item.id,
          item_code: item.item_code,
          item_description: item.item_description,
          revision: item.revision ?? undefined,
          item_category: item.item_category,
          quantity: toNumber(item.ordered_quantity || item.quantity), // Use ordered_quantity from database
          uom: item.uom,
          unit_price: toNumber(item.unit_price),
          discount_percentage: toNumber(item.discount_percentage),
          vat_applicable: item.vat_applicable,
          vat_percentage: toNumber(item.vat_percentage),
          notes: item.notes,
          specifications: item.specifications,
          delivery_days: item.delivery_days
        }));
        setLineItems(items);
      }
    }
  }, [isEditMode, salesOrder, form]);

  // Prefill department from accepted quotation (parent → child), like GRN from PO.
  useEffect(() => {
    if (isEditMode || !watchedQuotationId) return;
    const q = quotationsForSo.find((row) => row.id === watchedQuotationId);
    if (q?.department_id) {
      form.setFieldValue('department_id', q.department_id);
    }
  }, [isEditMode, watchedQuotationId, quotationsForSo, form]);

  // Calculate totals whenever line items or discount/vat change
  useEffect(() => {
    const calcSubtotal = lineItems.reduce((sum, item) => {
      const qty = Number(item.quantity) || 0;
      const price = Number(item.unit_price) || 0;
      return sum + (qty * price);
    }, 0);
    
    setSubtotal(calcSubtotal);

    const calcDiscountAmount = (calcSubtotal * discountPercentage) / 100;
    setDiscountAmount(calcDiscountAmount);

    const calcTotalAfterDiscount = calcSubtotal - calcDiscountAmount;
    setTotalAfterDiscount(calcTotalAfterDiscount);

    const calcVatAmount = vatApplicable ? (calcTotalAfterDiscount * vatPercentage) / 100 : 0;
    setVatAmount(calcVatAmount);

    const calcGrandTotal = calcTotalAfterDiscount + calcVatAmount;
    setGrandTotal(calcGrandTotal);
  }, [lineItems, discountPercentage, vatApplicable, vatPercentage]);

  const handleBack = () => {
    navigate('/growth/orders');
  };

  const handleAddLineItem = () => {
    const newItem: SalesOrderItemFormData = {
      key: `item-${Date.now()}`,
      item_code: '',
      item_description: '',
      revision: '',
      item_category: '',
      quantity: 1,
      uom: 'PCS',
      unit_price: 0,
      discount_percentage: 0,
      vat_applicable: true,
      vat_percentage: 5
    };
    setLineItems([...lineItems, newItem]);
  };

  const handleDeleteLineItem = (key: string) => {
    setLineItems(lineItems.filter(item => item.key !== key));
  };

  const handleLineItemChange = (key: string, field: string, value: any) => {
    setLineItems(lineItems.map(item => 
      item.key === key ? { ...item, [field]: value } : item
    ));
  };

  const handleAddMilestone = () => {
    setMilestoneRows([
      ...milestoneRows,
      {
        key: `ms-${Date.now()}`,
        milestone_number: milestoneRows.length + 1,
        milestone_name: '',
        milestone_value: 0,
        billing_percentage: 0,
        milestone_status: 'Planned',
      },
    ]);
  };

  const handleMilestoneChange = (key: string, field: keyof MilestoneRowData, value: any) => {
    setMilestoneRows(milestoneRows.map((m) =>
      m.key === key ? { ...m, [field]: value } : m
    ));
  };

  const handleDeleteMilestone = (key: string) => {
    setMilestoneRows(milestoneRows.filter((m) => m.key !== key));
  };

  const handleCustomerChange = (customerId: string) => {
    const customer = customers.find((c: any) => c.id === customerId);
    if (customer) {
      form.setFieldsValue({
        customer_name: customer.customer_name,
        customer_email: customer.email,
        customer_phone: customer.phone,
        billing_address: customer.billing_address,
        shipping_address: customer.shipping_address,
        project_id: undefined,
        project_name: undefined,
      });
    }
  };

  const handleSubmit = async (values: any) => {
    if (lineItems.length === 0) {
      message.error('Please add at least one line item');
      return;
    }

    const orderData: any = {
      customer_id: values.customer_id,
      customer_name: values.customer_name,
      ...(isEditMode ? {} : { quotation_id: values.quotation_id as string }),
      project_id: values.project_id || undefined,
      project_name: values.project_name || undefined,
      is_project_linked: Boolean(values.project_id),
      department_id: values.department_id,
      customer_contact: values.customer_contact,
      customer_email: values.customer_email,
      customer_phone: values.customer_phone,
      order_date: values.order_date ? values.order_date.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
      expected_delivery_date: values.expected_delivery_date ? values.expected_delivery_date.format('YYYY-MM-DD') : undefined,
      currency: values.currency || baseCurrency?.code || 'AED',
      payment_terms: values.payment_terms,
      delivery_terms: values.delivery_terms,
      customer_po_number: values.customer_po_number?.trim() || undefined,
      customer_po_date: values.customer_po_date
        ? values.customer_po_date.format('YYYY-MM-DD')
        : undefined,
      shipping_address: values.shipping_address,
      billing_address: values.billing_address,
      vat_applicable: values.vat_applicable,
      vat_percentage: values.vat_percentage,
      discount_percentage: values.discount_percentage || 0,
      subtotal: subtotal,
      discount_amount: discountAmount,
      vat_amount: vatAmount,
      grand_total: grandTotal,
      advance_payment_percentage: advancePaymentPercentage || 0,
      advance_payment_amount: advancePaymentPercentage > 0 ? (grandTotal * advancePaymentPercentage) / 100 : advancePaymentAmount,
      retention_percentage: retentionPercentage || 0,
      retention_amount: retentionPercentage > 0 ? (grandTotal * retentionPercentage) / 100 : retentionAmount,
      is_milestone_based: isMilestoneBased,
      notes: values.notes,
      special_instructions: values.special_instructions,
      revenue_service_type_id: values.revenue_service_type_id,
      commercial_scope_text: values.commercial_scope_text,
      quality_assurance_text: values.quality_assurance_text,
      customer_requirements_text: values.customer_requirements_text,
      ...(isEditMode && hasLinkedWorkOrders
        ? {
            amendment_reason: values.amendment_reason,
            workload_impact_note: values.workload_impact_note,
          }
        : {}),
      milestones: isMilestoneBased
        ? milestoneRows.map((m, i) => ({
            milestone_number: i + 1,
            milestone_name: m.milestone_name,
            description: m.description || undefined,
            planned_date: m.planned_date || undefined,
            milestone_value: m.milestone_value,
            billing_percentage: m.billing_percentage,
            milestone_status: m.milestone_status || 'Planned',
          }))
        : [],
      items: lineItems.map((item, index) => ({
        ...(item.id ? { id: item.id } : {}),
        line_number: index + 1,
        item_code: item.item_code,
        item_description: item.item_description,
        revision: item.revision?.trim() || null,
        item_category: item.item_category,
        ordered_quantity: item.quantity, // Map to ordered_quantity for backend
        uom: item.uom,
        unit_price: item.unit_price,
        discount_percentage: item.discount_percentage || 0,
        vat_applicable: item.vat_applicable,
        vat_percentage: item.vat_percentage,
        notes: item.notes,
        specifications: item.specifications,
        delivery_days: item.delivery_days
      }))
    };

    try {
      if (isEditMode) {
        await updateMutation.mutateAsync({ id: id!, data: orderData });
        message.success('Customer Order updated successfully');
        navigate(`/growth/orders/${id}`);
      } else {
        const response = await createMutation.mutateAsync(orderData);
        message.success('Customer Order created successfully');
        const newOrderId = (response as { data?: { id?: string } })?.data?.id;
        if (newOrderId) {
          navigate(`/growth/orders/${newOrderId}`);
        } else {
          navigate('/growth/orders');
        }
      }
    } catch (error: any) {
      console.error('Error saving sales order:', error);
      message.error(error.response?.data?.message || 'Failed to save customer order');
    }
  };

  if (orderLoading && isEditMode) {
    return (
      <div style={{ padding: '24px', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Spin size="large" tip="Loading customer order..." />
      </div>
    );
  }

  // Line items table columns
  const itemColumns: ColumnsType<SalesOrderItemFormData> = [
    {
      title: '#',
      key: 'index',
      width: 50,
      render: (_, __, index) => index + 1
    },
    {
      title: 'Item Code',
      dataIndex: 'item_code',
      key: 'item_code',
      width: 120,
      render: (value, record) => (
        <Input
          value={value}
          onChange={(e) => handleLineItemChange(record.key, 'item_code', e.target.value)}
          placeholder="Item Code"
          size="small"
        />
      )
    },
    {
      title: 'Description *',
      dataIndex: 'item_description',
      key: 'item_description',
      width: 200,
      render: (value, record) => (
        <Input.TextArea
          value={value}
          onChange={(e) => handleLineItemChange(record.key, 'item_description', e.target.value)}
          placeholder="Description"
          size="small"
          rows={2}
        />
      )
    },
    {
      title: 'Revision',
      dataIndex: 'revision',
      key: 'revision',
      width: 90,
      render: (value, record) => (
        <Input
          value={value}
          onChange={(e) => handleLineItemChange(record.key, 'revision', e.target.value)}
          placeholder="Rev"
          size="small"
          maxLength={50}
        />
      ),
    },
    {
      title: 'Quantity *',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      render: (value, record) => (
        <InputNumber
          value={value}
          onChange={(val) => handleLineItemChange(record.key, 'quantity', val || 0)}
          min={0}
          placeholder="Qty"
          size="small"
          style={{ width: '100%' }}
        />
      )
    },
    {
      title: 'UOM',
      dataIndex: 'uom',
      key: 'uom',
      width: 80,
      render: (value, record) => (
        <Select
          value={value}
          onChange={(val) => handleLineItemChange(record.key, 'uom', val)}
          size="small"
          style={{ width: '100%' }}
        >
          <Select.Option value="PCS">PCS</Select.Option>
          <Select.Option value="KG">KG</Select.Option>
          <Select.Option value="M">M</Select.Option>
          <Select.Option value="M2">M²</Select.Option>
          <Select.Option value="M3">M³</Select.Option>
          <Select.Option value="L">L</Select.Option>
          <Select.Option value="SET">SET</Select.Option>
          <Select.Option value="BOX">BOX</Select.Option>
        </Select>
      )
    },
    {
      title: 'Unit Price *',
      dataIndex: 'unit_price',
      key: 'unit_price',
      width: 120,
      render: (value, record) => (
        <InputNumber
          value={value}
          onChange={(val) => handleLineItemChange(record.key, 'unit_price', val || 0)}
          min={0}
          placeholder="Price"
          size="small"
          style={{ width: '100%' }}
          precision={2}
        />
      )
    },
    {
      title: 'Line Total',
      key: 'line_total',
      width: 120,
      render: (_, record) => {
        const qty = Number(record.quantity) || 0;
        const price = Number(record.unit_price) || 0;
        const total = qty * price;
        return <Text strong>{formatCurrency(total, orderCurrency)}</Text>;
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      render: (_, record) => (
        <Button
          type="text"
          danger
          size="small"
          icon={<DeleteOutlined />}
          onClick={() => handleDeleteLineItem(record.key)}
        />
      )
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
            Back to Customer Orders
          </Button>
        </div>

        <Card>
          <Title level={3}>
            {isEditMode ? 'Edit Customer Order' : 'Create Customer Order'}
          </Title>
          {isEditMode && salesOrder && (
            <Space>
              <Text strong>Order Number:</Text>
              <Tag color="blue">{salesOrder.sales_order_number}</Tag>
              <Text strong>Status:</Text>
              <Tag color={
                salesOrder.order_status === 'Confirmed' ? 'green' :
                salesOrder.order_status === 'Draft' ? 'default' :
                salesOrder.order_status === 'Processing' ? 'blue' :
                salesOrder.order_status === 'Shipped' ? 'cyan' :
                salesOrder.order_status === 'Delivered' ? 'success' :
                salesOrder.order_status === 'Cancelled' ? 'red' : 'default'
              }>
                {salesOrder.order_status}
              </Tag>
            </Space>
          )}
        </Card>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            order_date: dayjs(),
            currency: baseCurrency?.code || 'AED',
            vat_applicable: true,
            vat_percentage: 5,
            discount_percentage: 0,
            payment_terms: '30 Days',
            delivery_terms: 'FOB'
          }}
        >
          {/* Customer Information */}
          <Card title="Customer Information" style={{ marginBottom: '16px' }}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="customer_id"
                  label="Customer"
                  rules={[{ required: true, message: 'Please select a customer' }]}
                >
                  <Select
                    showSearch
                    placeholder="Select a customer"
                    optionFilterProp="children"
                    onChange={handleCustomerChange}
                    filterOption={(input, option) =>
                      String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    options={customers.map((customer: any) => ({
                      label: customer.customer_name,
                      value: customer.id
                    }))}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="customer_name"
                  label="Customer Name"
                  rules={[{ required: true, message: 'Please enter customer name' }]}
                >
                  <Input placeholder="Customer name" />
                </Form.Item>
              </Col>
            </Row>

            {!isEditMode && (
              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item
                    name="quotation_id"
                    label="Accepted quotation (from estimation or BOQ)"
                    rules={[
                      { required: true, message: 'Select an accepted quotation linked to an estimation or BOQ' },
                    ]}
                    extra="Customer orders must be prepared against an estimation. Only accepted quotations with an estimation or BOQ link are listed (not yet converted to a customer order)."
                  >
                    <Select
                      showSearch
                      placeholder={watchedCustomerId ? 'Select quotation' : 'Select customer first'}
                      optionFilterProp="label"
                      options={quotationOptions}
                      disabled={!watchedCustomerId}
                    />
                  </Form.Item>
                </Col>
              </Row>
            )}

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="customer_contact" label="Contact Person">
                  <Input placeholder="Contact person" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="customer_email" label="Email">
                  <Input placeholder="Email address" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="customer_phone" label="Phone">
                  <Input placeholder="Phone number" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="billing_address" label="Billing Address">
                  <TextArea rows={3} placeholder="Billing address" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="shipping_address"
                  label="Shipping Address"
                  rules={[{ required: true, whitespace: true, message: 'Shipping Address is required' }]}
                >
                  <TextArea rows={3} placeholder="Shipping address" />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* Order Details */}
          <Card title="Order Details" style={{ marginBottom: '16px' }}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="customer_po_number"
                  label="Customer PO"
                  rules={[{ required: true, message: 'Customer PO is required' }]}
                >
                  <Input placeholder="Customer PO / L.P.O number" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="customer_po_date"
                  label="Customer PO date"
                  rules={[{ required: true, message: 'Customer PO date is required' }]}
                >
                  <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="project_id" label="Project">
                  <ProjectSelector
                    allowClear
                    placeholder="Select project or create new"
                    customerId={watchedCustomerId}
                    customerName={
                      customers.find((c: any) => c.id === watchedCustomerId)?.customer_name as string | undefined
                    }
                    onChange={(_projectId, projectData) => {
                      form.setFieldValue(
                        'project_name',
                        projectData?.project_name ?? ''
                      );
                    }}
                  />
                </Form.Item>
                <Form.Item name="project_name" hidden>
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="department_id"
                  label="Department"
                  rules={[{ required: true, message: 'Department is required' }]}
                >
                  <DepartmentSelect />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="currency"
                  label="Currency"
                  rules={[{ required: true, message: 'Please select currency' }]}
                >
                  <Select>
                    <Select.Option value="AED">AED - UAE Dirham</Select.Option>
                    <Select.Option value="USD">USD - US Dollar</Select.Option>
                    <Select.Option value="EUR">EUR - Euro</Select.Option>
                    <Select.Option value="GBP">GBP - British Pound</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="revenue_service_type_id"
                  label="Revenue service type"
                  rules={[{ required: true, message: 'Required — delivery rules and invoice revenue GL' }]}
                >
                  <Select
                    showSearch
                    optionFilterProp="label"
                    placeholder="Select revenue / service type"
                    options={rstOptions}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="order_date"
                  label="Order Date"
                  rules={[{ required: true, message: 'Please select order date' }]}
                >
                  <DatePicker style={{ width: '100%' }} format="DD-MM-YYYY" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="expected_delivery_date" label="Expected Delivery Date">
                  <DatePicker style={{ width: '100%' }} format="DD-MM-YYYY" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="payment_terms" label="Payment Terms">
                  <Select>
                    <Select.Option value="Advance Payment">Advance Payment</Select.Option>
                    <Select.Option value="15 Days">15 Days</Select.Option>
                    <Select.Option value="30 Days">30 Days</Select.Option>
                    <Select.Option value="45 Days">45 Days</Select.Option>
                    <Select.Option value="60 Days">60 Days</Select.Option>
                    <Select.Option value="90 Days">90 Days</Select.Option>
                    <Select.Option value="COD">Cash on Delivery</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="delivery_terms" label="Delivery Terms">
                  <Select>
                    <Select.Option value="FOB">FOB - Free on Board</Select.Option>
                    <Select.Option value="CIF">CIF - Cost, Insurance & Freight</Select.Option>
                    <Select.Option value="EXW">EXW - Ex Works</Select.Option>
                    <Select.Option value="DDP">DDP - Delivered Duty Paid</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Divider orientation="left">Advance & Retention</Divider>
            <Row gutter={16}>
              <Col span={6}>
                <Form.Item label="Advance %">
                  <InputNumber
                    min={0}
                    max={100}
                    value={advancePaymentPercentage}
                    onChange={(v) => setAdvancePaymentPercentage(Number(v) || 0)}
                    style={{ width: '100%' }}
                    onBlur={() => {
                      if (advancePaymentPercentage > 0 && grandTotal > 0) {
                        setAdvancePaymentAmount((grandTotal * advancePaymentPercentage) / 100);
                      }
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label={`Advance Amount (${orderCurrency})`}>
                  <InputNumber
                    min={0}
                    value={advancePaymentAmount}
                    onChange={(v) => setAdvancePaymentAmount(Number(v) || 0)}
                    style={{ width: '100%' }}
                    precision={2}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="Retention %">
                  <InputNumber
                    min={0}
                    max={100}
                    value={retentionPercentage}
                    onChange={(v) => setRetentionPercentage(Number(v) || 0)}
                    style={{ width: '100%' }}
                    onBlur={() => {
                      if (retentionPercentage > 0 && grandTotal > 0) {
                        setRetentionAmount((grandTotal * retentionPercentage) / 100);
                      }
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label={`Retention Amount (${orderCurrency})`}>
                  <InputNumber
                    min={0}
                    value={retentionAmount}
                    onChange={(v) => setRetentionAmount(Number(v) || 0)}
                    style={{ width: '100%' }}
                    precision={2}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* Line Items */}
          <Card 
            title="Line Items" 
            style={{ marginBottom: '16px' }}
            extra={
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddLineItem}
              >
                Add Item
              </Button>
            }
          >
            <Table
              dataSource={lineItems}
              columns={itemColumns}
              pagination={false}
              scroll={{ x: 'max-content' }}
              locale={{ emptyText: 'No items added yet. Click "Add Item" to get started.' }}
            />
          </Card>

          {/* Financial Summary */}
          <Card title="Financial Summary" style={{ marginBottom: '16px' }}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="VAT Applicable" valuePropName="checked">
                  <Switch
                    checked={vatApplicable}
                    onChange={(checked) => {
                      setVatApplicable(checked);
                      form.setFieldValue('vat_applicable', checked);
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="vat_percentage"
                  label="VAT %"
                  rules={[{ required: vatApplicable, message: 'Please enter VAT percentage' }]}
                >
                  <InputNumber
                    min={0}
                    max={100}
                    style={{ width: '100%' }}
                    onChange={(value) => setVatPercentage(value || 0)}
                    disabled={!vatApplicable}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="discount_percentage" label="Discount %">
                  <InputNumber
                    min={0}
                    max={100}
                    style={{ width: '100%' }}
                    onChange={(value) => setDiscountPercentage(value || 0)}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Divider />

            <Row gutter={16}>
              <Col span={12} offset={12}>
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                  <Row justify="space-between">
                    <Text>Subtotal:</Text>
                    <Text strong>{formatCurrency(subtotal, orderCurrency)}</Text>
                  </Row>
                  <Row justify="space-between">
                    <Text>Discount ({discountPercentage}%):</Text>
                    <Text type="danger">- {formatCurrency(discountAmount, orderCurrency)}</Text>
                  </Row>
                  <Row justify="space-between">
                    <Text>Total After Discount:</Text>
                    <Text strong>{formatCurrency(totalAfterDiscount, orderCurrency)}</Text>
                  </Row>
                  {vatApplicable && (
                    <Row justify="space-between">
                      <Text>VAT ({vatPercentage}%):</Text>
                      <Text>{formatCurrency(vatAmount, orderCurrency)}</Text>
                    </Row>
                  )}
                  <Divider style={{ margin: '8px 0' }} />
                  <Row justify="space-between">
                    <Title level={4} style={{ margin: 0 }}>Grand Total:</Title>
                    <Title level={4} style={{ margin: 0, color: '#145A54' }}>
                      {formatCurrency(grandTotal, orderCurrency)}
                    </Title>
                  </Row>
                </Space>
              </Col>
            </Row>
          </Card>

          {/* Milestone-based order */}
          <Card title="Milestone stages" style={{ marginBottom: '16px' }}>
            <Form.Item label="Milestone-based order" valuePropName="checked">
              <Switch
                checked={isMilestoneBased}
                onChange={setIsMilestoneBased}
              />
            </Form.Item>
            {isMilestoneBased && (
              <>
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={handleAddMilestone}
                  style={{ marginBottom: 12 }}
                >
                  Add milestone
                </Button>
                <Table
                  dataSource={milestoneRows}
                  pagination={false}
                  size="small"
                  rowKey="key"
                  columns={[
                    { title: '#', width: 50, render: (_, __, i) => i + 1 },
                    {
                      title: 'Name',
                      dataIndex: 'milestone_name',
                      render: (val: string, r) => (
                        <Input
                          value={val}
                          onChange={(e) => handleMilestoneChange(r.key, 'milestone_name', e.target.value)}
                          placeholder="Milestone name"
                          size="small"
                        />
                      ),
                    },
                    {
                      title: 'Description',
                      dataIndex: 'description',
                      render: (val: string, r) => (
                        <Input
                          value={val}
                          onChange={(e) => handleMilestoneChange(r.key, 'description', e.target.value)}
                          placeholder="Description"
                          size="small"
                        />
                      ),
                    },
                    {
                      title: 'Planned date',
                      dataIndex: 'planned_date',
                      render: (val: string, r) => (
                        <Input
                          type="date"
                          value={val}
                          onChange={(e) => handleMilestoneChange(r.key, 'planned_date', e.target.value)}
                          size="small"
                        />
                      ),
                    },
                    {
                      title: 'Value',
                      dataIndex: 'milestone_value',
                      render: (val: number, r) => (
                        <InputNumber
                          min={0}
                          value={val}
                          onChange={(v) => handleMilestoneChange(r.key, 'milestone_value', Number(v) || 0)}
                          size="small"
                          style={{ width: '100%' }}
                          precision={2}
                        />
                      ),
                    },
                    {
                      title: 'Billing %',
                      dataIndex: 'billing_percentage',
                      render: (val: number, r) => (
                        <InputNumber
                          min={0}
                          max={100}
                          value={val}
                          onChange={(v) => handleMilestoneChange(r.key, 'billing_percentage', Number(v) || 0)}
                          size="small"
                          style={{ width: 80 }}
                        />
                      ),
                    },
                    {
                      title: 'Status',
                      dataIndex: 'milestone_status',
                      render: (val: string, r) => (
                        <Select
                          value={val}
                          onChange={(v) => handleMilestoneChange(r.key, 'milestone_status', v)}
                          size="small"
                          style={{ width: 120 }}
                          options={[
                            { value: 'Planned', label: 'Planned' },
                            { value: 'In Progress', label: 'In Progress' },
                            { value: 'Completed', label: 'Completed' },
                            { value: 'Cancelled', label: 'Cancelled' },
                          ]}
                        />
                      ),
                    },
                    {
                      title: '',
                      width: 60,
                      render: (_, r) => (
                        <Button
                          type="text"
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                          onClick={() => handleDeleteMilestone(r.key)}
                        />
                      ),
                    },
                  ]}
                />
              </>
            )}
          </Card>

          <Card title="Scope, quality & requirements" style={{ marginBottom: '16px' }}>
            <Paragraph type="secondary" style={{ marginTop: 0 }}>
              Captured on the customer order and copied to new work orders. Work orders can add independent notes on the WO screen (NM_WP_70).
            </Paragraph>
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item name="commercial_scope_text" label="Commercial / technical scope">
                  <TextArea rows={4} placeholder="Scope, drawings reference, deliverables" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="quality_assurance_text" label="Quality assurance">
                  <TextArea rows={3} placeholder="QA expectations" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="customer_requirements_text" label="Customer requirements">
                  <TextArea rows={3} placeholder="Requirements and constraints" />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* Additional Information */}
          <Card title="Additional Information" style={{ marginBottom: '16px' }}>
            {isEditMode && hasLinkedWorkOrders && (
              <Card type="inner" size="small" style={{ marginBottom: 16 }} title="Amendment (work orders exist)">
                <Paragraph type="secondary" style={{ marginTop: 0 }}>
                  Any save while work orders exist requires an amendment reason (NM_WP_68). Approval and sales review may reset until re-approved.
                </Paragraph>
                <Row gutter={16}>
                  <Col span={24}>
                    <Form.Item
                      name="amendment_reason"
                      label="Reason for change"
                      rules={[{ required: true, message: 'Required when work orders are linked' }]}
                    >
                      <TextArea rows={3} placeholder="Why is this customer order being changed?" />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item name="workload_impact_note" label="Workload / scope impact (optional)">
                      <TextArea rows={2} placeholder="Effect on production workload or schedule" />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            )}
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="notes" label="Notes">
                  <TextArea rows={4} placeholder="Internal notes" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="special_instructions" label="Special Instructions">
                  <TextArea rows={4} placeholder="Special instructions for this order" />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* Action Buttons */}
          <Card>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={createMutation.isPending || updateMutation.isPending}
                size="large"
              >
                {isEditMode ? 'Update Customer Order' : 'Create Customer Order'}
              </Button>
              <Button onClick={handleBack} size="large">
                Cancel
              </Button>
            </Space>
          </Card>
        </Form>
      </Space>
    </div>
  );
};

export default SalesOrderFormPage;
